import { Button } from "@chakra-ui/react";
import { useHypotheticalWaterfallOverviewRetrieveSuspense } from "gen/endpoints/hypothetical-waterfall/hypothetical-waterfall";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { ClickableElementLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FinancialReportingPage: React.FC = () => {
  const { t } = useTranslation(["financialReporting", "common", "admin"]);

  const { fundFamilyId } = useRequiredStringParams();
  const navigate = useNavigate();

  const { data } =
    useHypotheticalWaterfallOverviewRetrieveSuspense(fundFamilyId);

  const baseRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({});

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    (props): ClickableElementLinkInfo | undefined => {
      const metadata = props.row.data?.metadata;
      if (metadata?.type !== "hypothetical_waterfall") {
        return undefined;
      }

      return {
        type: "generic",
        to: baseRoute
          .hypotheticalWaterfall({
            hypotheticalWaterfallId: metadata.id,
            endOfPeriodReportId: metadata.end_of_period_report_id,
          })
          .review({})
          .waterfallDetails({}).$,
      };
    },
    [baseRoute],
  );

  const navigateToCreateHypoPage = () => navigate(baseRoute.hypoCreate({}).$);

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("common:financialReporting")}
        actions={
          <Restricted permission={PermissionTypeEnum.write}>
            <Button colorScheme="secondary" onClick={navigateToCreateHypoPage}>
              {t("financialReporting:runHypotheticalWaterfall")}
            </Button>
          </Restricted>
        }
      />
      <PageBody>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("financialReporting:hypotheticalEvents")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={data} getLinkInfoForRow={getLinkInfoForRow} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
